/**
 * Security Middleware & Interceptors
 * Handles rate limiting, request validation, and security headers
 */

import { dbRateLimiter, apiRateLimiter, authRateLimiter } from './rateLimiter';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ========================================
// REQUEST VALIDATION
// ========================================

export interface SecurityContext {
  userId: string;
  timestamp: number;
  requestId: string;
  ipHash?: string;
}

export class RequestValidator {
  private static readonly MAX_REQUEST_SIZE = 5 * 1024 * 1024; // 5MB
  private static readonly SUSPICIOUS_PATTERNS = [
    /(<script|javascript:|onerror|onclick)/gi,
    /(union|select|insert|delete|update|drop)\s+(from|into|table)/gi,
    /\.\.\//g, // Path traversal
  ];

  static validatePayload(data: unknown, maxSize: number = this.MAX_REQUEST_SIZE): boolean {
    if (!data) return false;

    const size = JSON.stringify(data).length;
    if (size > maxSize) {
      console.warn('Request payload exceeds maximum size:', size, '>', maxSize);
      return false;
    }

    const stringified = JSON.stringify(data).toLowerCase();
    for (const pattern of this.SUSPICIOUS_PATTERNS) {
      if (pattern.test(stringified)) {
        console.warn('Suspicious pattern detected in payload');
        return false;
      }
    }

    return true;
  }

  static sanitizeInput(input: string): string {
    return input
      .replace(/[<>]/g, '') // Remove angle brackets
      .trim()
      .slice(0, 1000); // Limit length
  }

  static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  }

  static validateURL(url: string): boolean {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }
}

// ========================================
// QUERY SECURITY WRAPPER
// ========================================

export class SecureQueryExecutor {
  static async executeWithRateLimit<T>(
    userId: string,
    queryFn: () => Promise<T>,
    queryType: 'db' | 'api' | 'auth' = 'db'
  ): Promise<T> {
    try {
      // Check rate limits
      const limiter = 
        queryType === 'auth' ? authRateLimiter :
        queryType === 'api' ? apiRateLimiter :
        dbRateLimiter;

      if (queryType === 'db') {
        const allowed = await (dbRateLimiter as any).checkLimit(userId);
        if (!allowed) {
          const quota = (dbRateLimiter as any).getRemainingQuota(userId);
          throw new Error(
            `Rate limit exceeded. Requests: ${quota.requests}, Tokens: ${quota.tokens}`
          );
        }
      } else if (!(limiter as any).isAllowed?.(userId)) {
        throw new Error(`Rate limit exceeded for ${queryType}`);
      }

      // Execute query with timeout
      return await this.executeWithTimeout(queryFn, 30000); // 30 second timeout
    } catch (error: any) {
      console.error(`[SecureQueryExecutor] ${queryType} query failed:`, error);
      throw error;
    }
  }

  private static executeWithTimeout<T>(
    fn: () => Promise<T>,
    timeoutMs: number
  ): Promise<T> {
    return Promise.race([
      fn(),
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Query timeout after ${timeoutMs}ms`)),
          timeoutMs
        )
      ),
    ]);
  }

  static async executeWithCircuitBreaker<T>(
    userId: string,
    queryFn: () => Promise<T>
  ): Promise<T> {
    return (dbRateLimiter as any).executeWithCircuitBreaker(userId, queryFn);
  }
}

// ========================================
// SECURITY HEADERS
// ========================================

export function setSecurityHeaders() {
  if (typeof document === 'undefined') return;

  // Content Security Policy via meta tag (when possible)
  let cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]') as HTMLMetaElement | null;
  if (!cspMeta) {
    cspMeta = document.createElement('meta');
    cspMeta.httpEquiv = 'Content-Security-Policy';
    cspMeta.content = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https://apis.google.com https://accounts.google.com https://cdn.jsdelivr.net",
      "worker-src 'self' blob:",
      "child-src 'self' blob:",
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com data:",
      "img-src 'self' data: https:",
      "connect-src 'self' https://supabase.co https://*.supabase.co wss://*.supabase.co https://accounts.google.com https://oauth2.googleapis.com https://www.googleapis.com https://firestore.googleapis.com https://www.gstatic.com https://identitytoolkit.googleapis.com https://securetoken.googleapis.com https://invisa-ai.firebaseapp.com https://api.elevenlabs.io wss://api.elevenlabs.io https://ai.gateway.lovable.dev",
      "frame-src 'self' https://accounts.google.com https://invisa-ai.firebaseapp.com",
      "media-src 'self' data:",
      "upgrade-insecure-requests",
    ].join('; ');
    document.head.appendChild(cspMeta);
  }

  // X-Content-Type-Options
  let xContentType = document.querySelector('meta[http-equiv="X-Content-Type-Options"]') as HTMLMetaElement | null;
  if (!xContentType) {
    xContentType = document.createElement('meta');
    xContentType.httpEquiv = 'X-Content-Type-Options';
    xContentType.content = 'nosniff';
    document.head.appendChild(xContentType);
  }

  // X-Frame-Options (via meta)
  let xFrameOptions = document.querySelector('meta[http-equiv="X-Frame-Options"]') as HTMLMetaElement | null;
  if (!xFrameOptions) {
    xFrameOptions = document.createElement('meta');
    xFrameOptions.httpEquiv = 'X-Frame-Options';
    xFrameOptions.content = 'SAMEORIGIN';
    document.head.appendChild(xFrameOptions);
  }

  // Referrer Policy
  let referrerPolicy = document.querySelector('meta[name="referrer"]') as HTMLMetaElement | null;
  if (!referrerPolicy) {
    referrerPolicy = document.createElement('meta');
    referrerPolicy.name = 'referrer';
    referrerPolicy.content = 'strict-origin-when-cross-origin';
    document.head.appendChild(referrerPolicy);
  }

  // Permissions Policy
  let permissionsPolicy = document.querySelector('meta[http-equiv="Permissions-Policy"]') as HTMLMetaElement | null;
  if (!permissionsPolicy) {
    permissionsPolicy = document.createElement('meta');
    permissionsPolicy.httpEquiv = 'Permissions-Policy';
    permissionsPolicy.content = [
      'camera=()',
      'microphone=(self)',
      'geolocation=()',
      'payment=()',
    ].join(', ');
    document.head.appendChild(permissionsPolicy);
  }
}

// ========================================
// SESSION SECURITY
// ========================================

export class SessionSecurityManager {
  private static readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
  private static readonly ACTIVITY_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
  private static idleTimer: ReturnType<typeof setTimeout> | null = null;
  private static lastActivityTime = Date.now();

  static initialize() {
    if (typeof window === 'undefined') return;

    // Track user activity
    const updateActivity = () => {
      SessionSecurityManager.lastActivityTime = Date.now();
      SessionSecurityManager.resetIdleTimer();
    };

    window.addEventListener('mousedown', updateActivity, { passive: true });
    window.addEventListener('keydown', updateActivity, { passive: true });
    window.addEventListener('touchstart', updateActivity, { passive: true });
    window.addEventListener('scroll', updateActivity, { passive: true });

    // Check for stale sessions periodically
    setInterval(() => {
      SessionSecurityManager.checkSessionValidity();
    }, 60000); // Check every minute
  }

  private static resetIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }

    this.idleTimer = setTimeout(() => {
      this.handleSessionTimeout();
    }, this.SESSION_TIMEOUT_MS);
  }

  private static checkSessionValidity() {
    supabase.auth.onAuthStateChange((event: string) => {
      if (event === 'SIGNED_OUT' || (event as any) === 'USER_DELETED') {
        this.handleSessionTimeout();
      }
    });
  }

  private static handleSessionTimeout() {
    console.warn('Session timeout - clearing data and redirecting to login');
    sessionStorage.clear();
    // Reset rate limiters
    (dbRateLimiter as any).reset();
    // Redirect to auth
    window.location.href = '/auth?reason=session_timeout';
  }

  static getSessionStatus() {
    const now = Date.now();
    const timeSinceActivity = now - this.lastActivityTime;
    const timeUntilTimeout = Math.max(0, this.SESSION_TIMEOUT_MS - timeSinceActivity);

    return {
      isActive: timeSinceActivity < this.ACTIVITY_THRESHOLD_MS,
      timeUntilTimeout,
      percentageRemaining: (timeUntilTimeout / this.SESSION_TIMEOUT_MS) * 100,
    };
  }
}

// ========================================
// DATA ENCRYPTION (Client-side)
// ========================================

export class DataEncryption {
  static encryptSensitiveData(data: string, key: string): string {
    try {
      // Use base64 encoding for client-side (not cryptographically secure)
      // For production, use TweetNaCl.js or libsodium.js
      const encoded = btoa(JSON.stringify({ data, timestamp: Date.now() }));
      return encoded;
    } catch (error) {
      console.error('Encryption failed:', error);
      return '';
    }
  }

  static decryptSensitiveData(encrypted: string, key: string): string | null {
    try {
      const decoded = atob(encrypted);
      const parsed = JSON.parse(decoded);
      return parsed.data;
    } catch (error) {
      console.error('Decryption failed:', error);
      return null;
    }
  }

  static hashPassword(password: string): string {
    // Firebase Auth handles password hashing server-side
    // This is just for local validation
    return btoa(password);
  }
}

// ========================================
// AUDIT LOGGING
// ========================================

export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  resource: string;
  changes?: Record<string, any>;
  ipHash?: string;
  timestamp: number;
  status: 'success' | 'failure';
}

export class AuditLogger {
  private static logs: AuditLog[] = [];
  private static readonly MAX_LOGS = 100;

  static log(
    userId: string,
    action: string,
    resource: string,
    status: 'success' | 'failure' = 'success',
    changes?: Record<string, any>
  ) {
    const log: AuditLog = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      userId,
      action,
      resource,
      changes,
      timestamp: Date.now(),
      status,
    };

    this.logs.push(log);

    // Keep only recent logs in memory
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }

    // Persist critical logs
    if (action === 'DELETE' || action === 'UPDATE' || status === 'failure') {
      this.persistLog(log);
    }

    console.debug(`[AuditLog] ${action} ${resource} by ${userId}:`, log);
  }

  private static persistLog(log: AuditLog) {
    // Send to server for persistent storage
    (supabase
      .from('audit_logs') as any)
      .insert([{ user_id: log.userId, action: log.action, details: { resource: log.resource, changes: log.changes, status: log.status, timestamp: log.timestamp } }])
      .then((res: any) => {
        if (res?.error) console.error('Failed to persist audit log:', res.error);
      }, (err: any) => console.error('Failed to persist audit log:', err));
  }

  static getLogs(userId?: string, limit: number = 50): AuditLog[] {
    let logs = [...this.logs];
    if (userId) {
      logs = logs.filter(log => log.userId === userId);
    }
    return logs.slice(-limit);
  }

  static clearLogs() {
    this.logs = [];
  }
}

// ========================================
// CSRF PROTECTION
// ========================================

export class CSRFProtection {
  private static token: string | null = null;

  static generateToken(): string {
    if (this.token) return this.token;

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 32; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    this.token = token;
    sessionStorage.setItem('csrf_token', token);
    return token;
  }

  static getToken(): string {
    if (!this.token) {
      this.token = sessionStorage.getItem('csrf_token') || this.generateToken();
    }
    return this.token;
  }

  static validateToken(token: string): boolean {
    const stored = this.getToken();
    return token === stored;
  }

  static refreshToken(): string {
    this.token = null;
    sessionStorage.removeItem('csrf_token');
    return this.generateToken();
  }
}

// ========================================
// INITIALIZE SECURITY
// ========================================

export function initializeSecurity() {
  console.log('[Security] Initializing security features...');
  
  setSecurityHeaders();
  SessionSecurityManager.initialize();
  
  // Generate initial CSRF token
  CSRFProtection.generateToken();

  console.log('[Security] Security features initialized');
}
